import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { analyticsAPI } from '../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { 
  Users, 
  Eye, 
  Activity, 
  TrendingUp, 
  Monitor, 
  Smartphone, 
  Tablet,
  Download,
  Calendar,
  RefreshCcw
} from 'lucide-react';

// Chart.js kayıt
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start_date && dateRange.end_date) {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }
      const response = await analyticsAPI.getDashboard(params);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtime = async () => {
    try {
      const response = await analyticsAPI.getRealtime();
      setRealtimeData(response.data);
    } catch (error) {
      console.error('Realtime fetch error:', error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchRealtime();

    // Her 30 saniyede bir realtime güncelle
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDateFilter = () => {
    fetchDashboard();
  };

  const handleExportData = () => {
    if (!dashboardData) return;
    
    const dataStr = JSON.stringify(dashboardData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-white text-xl">Analitik verileri yükleniyor...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-white text-xl">Veri bulunamadı</div>
      </div>
    );
  }

  const { summary, device_distribution, popular_pages, daily_traffic, popular_movies, popular_branches, genre_statistics } = dashboardData;

  // Grafik renk paleti
  const colors = {
    primary: '#fdb714',
    secondary: '#9333ea',
    success: '#10b981',
    info: '#3b82f6',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  // Günlük trafik grafiği
  const dailyTrafficChart = {
    labels: daily_traffic.map(d => d._id),
    datasets: [
      {
        label: 'Sayfa Görüntülenmeleri',
        data: daily_traffic.map(d => d.pageviews),
        borderColor: colors.primary,
        backgroundColor: `${colors.primary}33`,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Cihaz dağılımı grafiği
  const deviceLabels = {
    mobile: 'Mobil',
    desktop: 'Masaüstü',
    tablet: 'Tablet'
  };

  const deviceChart = {
    labels: device_distribution.map(d => deviceLabels[d._id] || d._id),
    datasets: [
      {
        label: 'Cihaz Dağılımı',
        data: device_distribution.map(d => d.count),
        backgroundColor: [colors.primary, colors.info, colors.secondary],
        borderWidth: 0,
      },
    ],
  };

  // Popüler filmler grafiği
  const moviesChart = {
    labels: popular_movies.slice(0, 10).map(m => m.title?.substring(0, 20) || 'N/A'),
    datasets: [
      {
        label: 'Görüntülenme',
        data: popular_movies.slice(0, 10).map(m => m.views),
        backgroundColor: colors.secondary,
      },
    ],
  };

  // Tür istatistikleri
  const genreChart = {
    labels: genre_statistics.slice(0, 8).map(g => g.genre),
    datasets: [
      {
        label: 'İzlenme',
        data: genre_statistics.slice(0, 8).map(g => g.views),
        backgroundColor: [
          colors.primary,
          colors.secondary,
          colors.info,
          colors.success,
          colors.warning,
          colors.danger,
          '#8b5cf6',
          '#ec4899',
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#fff',
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af' },
        grid: { color: '#374151' },
      },
      y: {
        ticks: { color: '#9ca3af' },
        grid: { color: '#374151' },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#fff',
        },
        position: 'bottom',
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">📊 Gelişmiş Analitik Dashboard</h1>
            <p className="text-gray-400 mt-1">Site performansınızı ve kullanıcı davranışlarını analiz edin</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchDashboard} className="bg-yellow-500 hover:bg-yellow-600">
              <RefreshCcw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button onClick={handleExportData} variant="outline" className="text-white border-white">
              <Download className="w-4 h-4 mr-2" />
              Dışa Aktar
            </Button>
          </div>
        </div>

        {/* Tarih Filtresi */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-white text-sm mb-2 block">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
                />
              </div>
              <div className="flex-1">
                <label className="text-white text-sm mb-2 block">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
                />
              </div>
              <Button onClick={handleDateFilter} className="bg-yellow-500 hover:bg-yellow-600">
                <Calendar className="w-4 h-4 mr-2" />
                Filtrele
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Özet Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-600 to-blue-800 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm font-normal">Toplam Sayfa Görüntüleme</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-white">{summary.total_pageviews.toLocaleString()}</div>
                <Eye className="w-8 h-8 text-white opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-600 to-purple-800 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm font-normal">Toplam Event</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-white">{summary.total_events.toLocaleString()}</div>
                <Activity className="w-8 h-8 text-white opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-600 to-green-800 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm font-normal">Canlı Ziyaretçi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-white">{summary.live_visitors}</div>
                <Users className="w-8 h-8 text-white opacity-50" />
              </div>
              <p className="text-xs text-white opacity-75 mt-1">Son 5 dakika</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-600 to-orange-800 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm font-normal">Admin İşlem</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-white">{summary.total_admin_actions}</div>
                <TrendingUp className="w-8 h-8 text-white opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Grafikler */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Günlük Trafik */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">📈 Günlük Trafik Trendi</CardTitle>
              <CardDescription className="text-gray-400">Son 30 günün sayfa görüntüleme trendi</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: '300px' }}>
                <Line data={dailyTrafficChart} options={chartOptions} />
              </div>
            </CardContent>
          </Card>

          {/* Cihaz Dağılımı */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">📱 Cihaz Dağılımı</CardTitle>
              <CardDescription className="text-gray-400">Kullanıcıların cihaz tercihleri</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: '300px' }}>
                <Doughnut data={deviceChart} options={doughnutOptions} />
              </div>
            </CardContent>
          </Card>

          {/* Popüler Filmler */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">🎬 En Popüler Filmler</CardTitle>
              <CardDescription className="text-gray-400">En çok görüntülenen filmler</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: '300px' }}>
                <Bar data={moviesChart} options={chartOptions} />
              </div>
            </CardContent>
          </Card>

          {/* Tür İstatistikleri */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">🎭 Tür İstatistikleri</CardTitle>
              <CardDescription className="text-gray-400">Popüler film türleri</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: '300px' }}>
                <Doughnut data={genreChart} options={doughnutOptions} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Popüler Sayfalar */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">🔥 Popüler Sayfalar</CardTitle>
            <CardDescription className="text-gray-400">En çok ziyaret edilen sayfalar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-white py-3 px-4">Sayfa Yolu</th>
                    <th className="text-left text-white py-3 px-4">Başlık</th>
                    <th className="text-right text-white py-3 px-4">Görüntülenme</th>
                  </tr>
                </thead>
                <tbody>
                  {popular_pages.map((page, idx) => (
                    <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700">
                      <td className="py-3 px-4 text-gray-300 font-mono text-sm">{page._id}</td>
                      <td className="py-3 px-4 text-gray-300">{page.title || '-'}</td>
                      <td className="py-3 px-4 text-yellow-500 font-semibold text-right">{page.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Popüler Şubeler */}
        {popular_branches && popular_branches.length > 0 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">🏢 Popüler Şubeler</CardTitle>
              <CardDescription className="text-gray-400">En çok ilgi gören sinema salonları</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {popular_branches.map((branch, idx) => (
                  <div key={idx} className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-white font-semibold">{branch.name}</h3>
                    <p className="text-gray-400 text-sm">{branch.city}</p>
                    <p className="text-yellow-500 font-bold mt-2">{branch.views} görüntülenme</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gerçek Zamanlı Aktiviteler */}
        {realtimeData && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Gerçek Zamanlı Aktiviteler
              </CardTitle>
              <CardDescription className="text-gray-400">Son 5 dakikadaki kullanıcı hareketleri</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {realtimeData.recent_pageviews.slice(0, 10).map((pv, idx) => (
                  <div key={idx} className="bg-gray-700 p-3 rounded text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">{pv.page_path}</span>
                      <span className="text-gray-500 text-xs">{new Date(pv.timestamp).toLocaleTimeString('tr-TR')}</span>
                    </div>
                    {pv.device_type && (
                      <div className="text-gray-400 text-xs mt-1">
                        {pv.device_type === 'mobile' && <Smartphone className="inline w-3 h-3 mr-1" />}
                        {pv.device_type === 'tablet' && <Tablet className="inline w-3 h-3 mr-1" />}
                        {pv.device_type === 'desktop' && <Monitor className="inline w-3 h-3 mr-1" />}
                        {pv.device_type}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
